import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { SalespeopleTable } from "./SalespeopleTable";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../ui/sheet";
import { money2, count as fmt, dt, pct1 } from "../lux";
import { humanMinutes } from "../PeriodPicker";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

// SALES-033: the Salespeople tab makes ONE request for the salespeople data.
// This query key is shared with the page-level money gate (SalesPerformance.jsx),
// so react-query dedupes both subscribers to a single network call per load.
export const salespeopleQuery = (range) => ({
  queryKey: ["sp-salespeople", range.from, range.to],
  queryFn: async () => (await api.get("/sales-performance/salespeople", {
    params: { from: range.from || undefined, to: range.to || undefined },
  })).data,
});

const shortDay = (iso) => (iso ? dt(iso, { month: "short", day: "numeric" }) : "—");

// SALES-033 msg_8 item 6: no icon (the reference has none), and the label on
// one line, so all five cards sit on one baseline at 1280 and 1440.
const LeaderCard = ({ label, leader, fmtValue, testid }) => (
  <div className="border border-border bg-card p-5" data-testid={testid}>
    <div className="label-mono mb-4 whitespace-nowrap">{label}</div>
    {leader ? (
      <>
        <div className="font-heading text-2xl truncate">{leader.salesperson}</div>
        <div className="tnum text-sm text-primary mt-1">{fmtValue(leader)}</div>
      </>
    ) : (
      <div className="text-sm text-muted-foreground">No data in this period</div>
    )}
  </div>
);

function Drawer({ name, range, onClose }) {
  const { data } = useQuery({
    queryKey: ["sales-person", name, range.from, range.to],
    queryFn: async () => (await api.get(`/sales-performance/salespeople/${encodeURIComponent(name)}`, {
      params: { from: range.from || undefined, to: range.to || undefined },
    })).data,
    enabled: !!name,
  });
  // SALES-020: this drawer printed revenue for EVERYBODY. The server now
  // withholds the figures; this hides the places they were shown. Fail-closed
  // on purpose (`=== true`), so an unresolved response never reveals money.
  const showMoney = data?.money_visible === true;
  const chart = (data?.monthly || []).map((m) => ({ month: m.month, Revenue: m.revenue, Bookings: m.bookings }));
  const made = data?.bookings_made || [];
  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-2xl bg-card border-border overflow-y-auto" data-testid="salesperson-drawer">
        <SheetHeader>
          <SheetTitle className="font-heading text-2xl">{name}</SheetTitle>
          <SheetDescription>Monthly trend, bookings made, and the latest bookings and leads for the selected range.</SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-7">
          {showMoney && (
          <div className="border border-border p-3">
            {chart.length === 0 ? (
              <div className="text-sm text-muted-foreground py-10 text-center">No bookings in this range.</div>
            ) : (
              <ResponsiveContainer width="100%" height={190}>
                <BarChart data={chart} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} />
                  <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v, k) => (k === "Revenue" ? money2(v) : v)} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                  <Bar dataKey="Revenue" fill="hsl(var(--primary))" radius={[3, 3, 0, 0]} maxBarSize={34} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
          )}

          {/* SALES-033 scope 6: bookings made in the period, each tagged from-a-lead
              or own, plus how many were for a customer with no earlier booking. */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="label-mono">Bookings made</div>
              <div className="label-mono" data-testid="drawer-new-clients">
                New clients: {data?.new_clients == null ? "—" : fmt(data.new_clients)}
              </div>
            </div>
            <div className="border border-border divide-y divide-border/50 max-h-72 overflow-y-auto" data-testid="drawer-bookings-made">
              {made.length === 0 && <div className="p-5 text-sm text-muted-foreground text-center">None in range.</div>}
              {made.map((b) => (
                <a key={b.id} href={`/bookings/view/${b.id}`} className="flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-white/[0.03]">
                  <span className="tnum text-xs text-muted-foreground w-20 shrink-0">{shortDay(b.date)}</span>
                  <span className="flex-1 min-w-0 truncate">{b.vehicle || "—"}</span>
                  <span className="label-mono shrink-0">{b.from_lead ? "From lead" : "Own"}{b.new_client ? " · New" : ""}</span>
                </a>
              ))}
            </div>
          </div>

          <div>
            <div className="label-mono mb-2">Last bookings</div>
            <div className="border border-border divide-y divide-border/50 max-h-72 overflow-y-auto">
              {(data?.bookings || []).length === 0 && <div className="p-5 text-sm text-muted-foreground text-center">None in range.</div>}
              {(data?.bookings || []).map((b) => (
                <a key={b.id} href={`/bookings/view/${b.id}`} className="flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-white/[0.03]">
                  <span className="tnum text-xs text-muted-foreground w-20 shrink-0">{b.date}</span>
                  <span className="flex-1 min-w-0 truncate">{b.customer || "Guest"}<span className="text-muted-foreground"> · {b.vehicle || "—"}</span></span>
                  {showMoney && <span className="tnum text-primary">{money2(b.revenue)}</span>}
                </a>
              ))}
            </div>
          </div>

          <div>
            <div className="label-mono mb-2">Last leads</div>
            <div className="border border-border divide-y divide-border/50 max-h-72 overflow-y-auto">
              {(data?.leads || []).length === 0 && <div className="p-5 text-sm text-muted-foreground text-center">No leads.</div>}
              {(data?.leads || []).map((l) => (
                <div key={l.id} className="flex items-center gap-3 px-4 py-2.5 text-sm">
                  <span className="tnum text-xs text-muted-foreground w-24 shrink-0">{dt(l.created_at, { month: "short", day: "numeric" })}</span>
                  <span className="flex-1 min-w-0 truncate">{l.customer || "—"}<span className="text-muted-foreground"> · {l.source || "—"}</span></span>
                  <span className="text-xs text-muted-foreground">{humanMinutes(l.response_minutes)}</span>
                  <span className="label-mono">{l.status}{l.lost_reason ? ` · ${l.lost_reason}` : ""}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export function SalespeopleTab({ range, moneyOk, initialPerson }) {
  const [open, setOpen] = useState(initialPerson || null);
  const { data, isLoading } = useQuery(salespeopleQuery(range));
  const lb = data?.leaderboard || {};

  // SALES-028: points per rep for the SAME period the picker sets. A separate
  // dataset from a separate endpoint — the "one request" rule is about the
  // salespeople data, which the table and the money gate share above.
  const { data: pointsData } = useQuery({
    queryKey: ["sales-points", range.from, range.to],
    queryFn: async () => (await api.get("/sales-performance/points", {
      params: { from: range.from || undefined, to: range.to || undefined },
    })).data,
  });
  const pointsByRep = {};
  (pointsData?.rows || []).forEach((r) => { pointsByRep[(r.salesperson || "").toLowerCase()] = r.points; });
  const rows = (data?.rows || []).map((r) => ({ ...r, points: pointsByRep[(r.salesperson || "").toLowerCase()] || 0 }));

  const cardCols = moneyOk ? "xl:grid-cols-5" : "xl:grid-cols-4";
  // SALES-033 msg_8 item 9: the CSV file name carries the period. "start" or
  // "today" fills a missing side; no dates at all -> all-time.
  const exportName = (!range.from && !range.to)
    ? "salespeople_all-time"
    : `salespeople_${range.from || "start"}_to_${range.to || "today"}`;

  return (
    <div className="space-y-6">
      <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 ${cardCols} gap-4`}>
        <LeaderCard testid="leader-cars" label="Most cars out"
          leader={lb.most_cars_out} fmtValue={(l) => `${fmt(l.value)} cars out`} />
        <LeaderCard testid="leader-bookings" label="Most bookings made"
          leader={lb.most_bookings_made} fmtValue={(l) => `${fmt(l.value)} bookings`} />
        <LeaderCard testid="leader-replied" label="Most leads replied"
          leader={lb.most_leads_replied} fmtValue={(l) => `${fmt(l.value)} replies`} />
        <LeaderCard testid="leader-close" label="Best lead close"
          leader={lb.best_lead_close} fmtValue={(l) => `${pct1(l.value)} · ${fmt(l.won)} of ${fmt(l.of)}`} />
        {moneyOk && (
          <LeaderCard testid="leader-revenue" label="Most revenue"
            leader={lb.most_revenue} fmtValue={(l) => money2(l.value)} />
        )}
      </div>

      <SalespeopleTable
        rows={rows}
        moneyOk={moneyOk}
        trackingStart={data?.tracking_start}
        periodEnd={range.to}
        loading={isLoading}
        exportName={exportName}
        onRowClick={(r) => setOpen(r.salesperson)}
      />

      {open && <Drawer name={open} range={range} onClose={() => setOpen(null)} />}
    </div>
  );
}
